/**
 * Lessons Index - Auto-generated catalog from skill area modules
 *
 * This file aggregates all lessons from skill-area-organized modules
 * and attaches narratives to create the final microSessionCatalog.
 */

import { communicationLessons } from './communication';
import { operationsLessons } from './operations';
import { leadershipLessons } from './leadership';
import { insightLessons } from './insight';
import { foundationsLessons } from './foundations';
import { automationLessons } from './automation';
import { getNarrative } from '../microSessionNarratives';
import type { MicroSession } from '@/types/micro';
import type { LessonDefinition } from './types';

// Re-export types for consumers
export type { MicroSession, LessonDefinition };
export type { MicroSessionFormat, Narrative, ContextScenario, SkillArea, LearningMode } from './types';

// Re-export skill area modules
export * from './communication';
export * from './operations';
export * from './leadership';
export * from './insight';
export * from './foundations';
export * from './automation';

/**
 * Attaches narrative to a lesson definition to create a full MicroSession
 */
const attachNarrative = (lesson: LessonDefinition): MicroSession => ({
  ...lesson,
  narrative: getNarrative(lesson.id),
});

/**
 * All lessons organized by skill area
 */
export const lessonsBySkillArea = {
  Communication: communicationLessons,
  Operations: operationsLessons,
  Leadership: leadershipLessons,
  Insight: insightLessons,
  Foundations: foundationsLessons,
  Automation: automationLessons,
} as const;

/**
 * Complete catalog of all micro sessions with narratives attached
 * This is the primary export for consumers that need all lessons
 */
export const microSessionCatalog: MicroSession[] = [
  ...foundationsLessons,
  ...communicationLessons,
  ...operationsLessons,
  ...leadershipLessons,
  ...insightLessons,
  ...automationLessons,
].map(attachNarrative);

/**
 * Get a specific lesson by ID
 */
export const getLessonById = (id: string): MicroSession | undefined => {
  return microSessionCatalog.find(session => session.id === id);
};

/**
 * Get all lessons for a specific skill area
 */
export const getLessonsBySkillArea = (skillArea: keyof typeof lessonsBySkillArea): MicroSession[] => {
  return lessonsBySkillArea[skillArea].map(attachNarrative);
};

/**
 * Get count of lessons per skill area
 */
export const lessonCounts = {
  Communication: communicationLessons.length,
  Operations: operationsLessons.length,
  Leadership: leadershipLessons.length,
  Insight: insightLessons.length,
  Foundations: foundationsLessons.length,
  Automation: automationLessons.length,
  total: microSessionCatalog.length,
} as const;

/**
 * Get all sessions eligible for daily rotation
 */
export const getDailyEligibleSessions = () =>
  microSessionCatalog.filter((session) => session.dailyEligible);

/**
 * Get most recently released sessions
 */
export const getRecentlyReleasedSessions = (limit = 3) =>
  [...microSessionCatalog]
    .sort((a, b) => Date.parse(b.releasedAt) - Date.parse(a.releasedAt))
    .slice(0, limit);
